<template>
    <div class="date-picker-wrapper">
        <tabs v-if="tabOptions.length" v-model:value="activeTab" @update:value="setActiveTab">
            <tab-list>
                <tab v-for="tab in tabOptions" :key="tab.id" :value="tab.id">
                    {{ tab.name }}
                </tab>
            </tab-list>
        </tabs>
        <div
            class="date-selector"
            :class="{
                compact: activeTab === AdvancedDatePickerTabsEnum.DATE,
                'boa-period': activeTab === AdvancedDatePickerTabsEnum.BOA_PERIOD,
            }"
        >
            <date-picker-panel
                ref="datePickerPanelRef"
                v-show="activeTab === AdvancedDatePickerTabsEnum.DATE"
                :model-value="props.modelValue"
                :default-model-value="props.defaultModelValue"
                :days-only="props.daysOnly"
                :date-selection-locked="props.dateSelectionLocked"
                :only-show-single-day="props.onlyShowSingleDay"
                :minimum-view="props.minimumView"
                :min-date="props.minDate"
                :max-date="props.maxDate"
            />
            <granularity-date-picker-panel
                v-if="activeTab === AdvancedDatePickerTabsEnum.INDEX_GRANULARITY"
                v-model="aggregate"
                index-granularity
                :selection-locked="props.granularityLocked"
                :index-granularity-warning="props.indexGranularityWarning"
            />
            <granularity-date-picker-panel
                v-if="activeTab === AdvancedDatePickerTabsEnum.GRANULARITY"
                v-model="aggregate"
                :selection-locked="props.granularityLocked"
                :index-granularity-warning="props.indexGranularityWarning"
            />
            <timezone-date-picker-panel
                ref="timezoneDatePickerPanelRef"
                v-if="props.allowSelectTimezone"
                v-show="activeTab === AdvancedDatePickerTabsEnum.TIMEZONE"
                :timezone-id="props.timezoneId"
                :period-frequency="props.periodFrequency"
                :timezone-selection-locked="props.timezoneSelectionLocked"
                :period-frequency-selection-locked="props.periodFrequencySelectionLocked"
                :sitewide-timezone="sitewideTimezone"
                :sitewide-periods-per-day="sitewidePeriodsPerDay"
                :sitewide-periods-per-day-label="sitewidePeriodsPerDayLabel"
                :hide-period-frequency="props.hidePeriodFrequency"
            />
            <boa-date-picker-panel
                ref="boaDatePickerPanelRef"
                v-if="showBoaPeriodPanel"
                v-show="activeTab === AdvancedDatePickerTabsEnum.BOA_PERIOD"
                :model-value="props.boaDatePeriod!"
                :period-override-mode="props.boaPeriodOverrideMode"
                :selection-locked="props.boaPeriodSelectionLocked"
            />
        </div>
        <div class="actions">
            <primevue-button v-if="props.resettable" class="plain-btn" @click="reset">
                Reset
                <font-awesome-icon :icon="['fas', 'rotate-left']" />
            </primevue-button>
            <primevue-button v-if="props.clearable" class="plain-btn" @click="clearSelection">
                Clear date filters
                <font-awesome-icon :icon="['fas', 'ban']" />
            </primevue-button>
            <primevue-button v-tooltip="updateDisabledReason" class="plain-btn" :disabled="isUpdateDisabled" @click="submitUpdate">
                {{ updateButtonLabel }}
                <font-awesome-icon :icon="['fas', 'sync']" />
            </primevue-button>
        </div>
    </div>
</template>

<script lang="ts" setup>
import {
    computed, ref, watch,
} from 'vue';
import PrimevueButton from 'primevue/button';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import { IAdvancedDatePickerModel } from '@/interfaces/general/IAdvancedDatePickerModel.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { ICombinedAggregateOptions } from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { PERIOD_FREQUENCY_OPTIONS } from '@/constants/PeriodFrequencyOptions.ts';
import { TDatePickerMinimumView } from '@/types/powerMarkets/TDatePickerMinimumView.ts';
import { TDatePickerGranularityMode } from '@/types/powerMarkets/TDatePickerGranularityMode.ts';
import { AdvancedDatePickerTabsEnum } from '@/enums/general/AdvancedDatePickerTabsEnum.ts';
import TimezoneDatePickerPanel from '@/components/powerMarkets/date-picker/TimezoneDatePickerPanel.vue';
import GranularityDatePickerPanel from '@/components/powerMarkets/date-picker/GranularityDatePickerPanel.vue';
import DatePickerPanel from '@/components/powerMarkets/date-picker/DatePickerPanel.vue';
import BoaDatePickerPanel from '@/components/powerMarkets/date-picker/BoaDatePickerPanel.vue';
import { IDatePeriod } from '@/interfaces/powerMarkets/balancing/IDatePeriod.ts';
import { PeriodTypeOverridesWithInherited } from '@/types/powerMarkets/balancing/PeriodTypeOverrides.ts';

const props = defineProps<{
    modelValue: IAdvancedDatePickerModel | null;
    defaultModelValue?: IAdvancedDatePickerModel | null;
    daysOnly?: boolean;
    timezoneId?: string;
    periodFrequency?: number;
    allowSelectTimezone?: boolean;
    dateSelectionLocked?: boolean;
    timezoneSelectionLocked?: boolean;
    periodFrequencySelectionLocked?: boolean;
    granularityLocked?: boolean;
    granularityMode?: TDatePickerGranularityMode;
    indexGranularityWarning?: string;
    onlyShowSingleDay?: boolean;
    minimumView?: TDatePickerMinimumView;
    minDate?: Date;
    maxDate?: Date;
    clearable?: boolean;
    resettable?: boolean;
    allowedTabs?: Array<AdvancedDatePickerTabsEnum>;
    hidePeriodFrequency?: boolean;
    allowSelectBoaPeriod?: boolean;
    boaDatePeriod?: IDatePeriod;
    boaPeriodOverrideMode?: PeriodTypeOverridesWithInherited;
    boaPeriodSelectionLocked?: boolean;
}>();

const emit = defineEmits<{
    submit: [IAdvancedDatePickerModel, AdvancedDatePickerTabsEnum];
    'submit:boa-period': [IDatePeriod, PeriodTypeOverridesWithInherited];
    reset: [];
    'reset:filters': [];
    'close:picker': [];
}>();

const userSettingsStore = useUserSettingsStore();

const defaultAggregateOptions: ICombinedAggregateOptions = {
    granularity: null,
    type: null,
    cumulative: false,
    delta: null,
};

const aggregateFromModel = props.modelValue?.aggregate ?? null;
const aggregate = ref<ICombinedAggregateOptions>({
    ...(aggregateFromModel ?? defaultAggregateOptions),
});

const datePickerPanelRef = ref<InstanceType<typeof DatePickerPanel> | null>(null);
const timezoneDatePickerPanelRef = ref<InstanceType<typeof TimezoneDatePickerPanel> | null>(null);
const boaDatePickerPanelRef = ref<InstanceType<typeof BoaDatePickerPanel> | null>(null);

const sitewideTimezone = computed<string>(() => userSettingsStore.sitewideTimezone);
const sitewidePeriodsPerDay = computed<number>(() => userSettingsStore.sitewidePeriodsPerDay);
const sitewidePeriodsPerDayLabel = computed<string>(() => PERIOD_FREQUENCY_OPTIONS.find(option => option.id === sitewidePeriodsPerDay.value.toString())?.name || 'unknown');
const showBoaPeriodPanel = computed<boolean>(() => !!props.allowSelectBoaPeriod && !!props.boaDatePeriod);

const activeTab = ref<AdvancedDatePickerTabsEnum>(AdvancedDatePickerTabsEnum.DATE);

const tabOptions = computed<Array<IIdName>>(() => {
    const options: Array<IIdName> = [];

    if (tabIsAllowed(AdvancedDatePickerTabsEnum.DATE)) {
        options.push({ id: AdvancedDatePickerTabsEnum.DATE, name: 'Date' });
    }

    if (props.granularityMode === 'index' && tabIsAllowed(AdvancedDatePickerTabsEnum.INDEX_GRANULARITY)) {
        options.push({ id: AdvancedDatePickerTabsEnum.INDEX_GRANULARITY, name: 'Granularity' });
    } else if (props.granularityMode === 'standard' && tabIsAllowed(AdvancedDatePickerTabsEnum.GRANULARITY)) {
        options.push({ id: AdvancedDatePickerTabsEnum.GRANULARITY, name: 'Granularity' });
    }

    if (props.allowSelectBoaPeriod && tabIsAllowed(AdvancedDatePickerTabsEnum.BOA_PERIOD)) {
        options.push({ id: AdvancedDatePickerTabsEnum.BOA_PERIOD, name: 'Period' });
    }

    if (props.allowSelectTimezone && tabIsAllowed(AdvancedDatePickerTabsEnum.TIMEZONE)) {
        options.push({ id: AdvancedDatePickerTabsEnum.TIMEZONE, name: 'Timezone' });
    }

    return options;
});
const isUpdateDisabled = computed<boolean>(() => {
    if (activeTab.value === AdvancedDatePickerTabsEnum.DATE) return !!props.dateSelectionLocked;
    if (
        activeTab.value === AdvancedDatePickerTabsEnum.GRANULARITY
        || activeTab.value === AdvancedDatePickerTabsEnum.INDEX_GRANULARITY
    ) return !!props.granularityLocked;
    if (activeTab.value === AdvancedDatePickerTabsEnum.TIMEZONE) {
        if (props.hidePeriodFrequency) return !!props.timezoneSelectionLocked;
        return !!(props.timezoneSelectionLocked && props.periodFrequencySelectionLocked);
    }
    if (activeTab.value === AdvancedDatePickerTabsEnum.BOA_PERIOD) return !!props.boaPeriodSelectionLocked;
    return false;
});

const updateButtonLabel = computed<string>(() => {
    if (
        activeTab.value === AdvancedDatePickerTabsEnum.DATE
        || activeTab.value === AdvancedDatePickerTabsEnum.GRANULARITY
        || activeTab.value === AdvancedDatePickerTabsEnum.INDEX_GRANULARITY
    ) return 'Update date options';
    if (activeTab.value === AdvancedDatePickerTabsEnum.BOA_PERIOD) return 'Update period options';
    return `Update ${activeTab.value} options`;
});

const updateDisabledReason = computed<string>(() => {
    if (!isUpdateDisabled.value) return '';
    if (activeTab.value === AdvancedDatePickerTabsEnum.DATE) return 'Dates are overridden by the dashboard.';
    if (activeTab.value === AdvancedDatePickerTabsEnum.TIMEZONE) {
        if (props.hidePeriodFrequency && props.timezoneSelectionLocked) return 'Timezone is set at the dashboard level.';
        if (props.timezoneSelectionLocked && props.periodFrequencySelectionLocked) return 'Timezone and period frequency are set at the dashboard level.';
        if (props.timezoneSelectionLocked) return 'Timezone is set at the dashboard level.';
        if (props.periodFrequencySelectionLocked) return 'Period frequency is set at the dashboard level.';
    }
    if (
        activeTab.value === AdvancedDatePickerTabsEnum.GRANULARITY
        || activeTab.value === AdvancedDatePickerTabsEnum.INDEX_GRANULARITY
    ) return 'Granularity is set at the dashboard level.';
    if (activeTab.value === AdvancedDatePickerTabsEnum.BOA_PERIOD) return 'Period is set at the dashboard level.';
    return 'Action is disabled.';
});

function clearSelection (): void {
    datePickerPanelRef.value?.clearSelection();
    emit('reset:filters');
}

function reset (): void {
    emit('reset');
}

function setActiveTab (tab: string | number): void {
    const advancedDatePickerTab = tab as AdvancedDatePickerTabsEnum;

    activeTab.value = advancedDatePickerTab;
}

function tabIsAllowed (tab: AdvancedDatePickerTabsEnum): boolean {
    return props.allowedTabs ? props.allowedTabs.includes(tab) : true;
}

function getPayload (): IAdvancedDatePickerModel {
    const datePayload = datePickerPanelRef.value!.getPayload();
    const timezonePayload = timezoneDatePickerPanelRef.value?.getPayload();

    return {
        aggregate: { ...aggregate.value },
        ...datePayload,
        timezone: timezonePayload?.timezone ?? null,
        periodsPerDay: timezonePayload?.periodsPerDay ?? null,
    };
}

function submitUpdate (): void {
    if (isUpdateDisabled.value) return;

    if (activeTab.value === AdvancedDatePickerTabsEnum.BOA_PERIOD) {
        const boaPayload = boaDatePickerPanelRef.value?.getPayload();
        if (boaPayload) {
            emit('submit:boa-period', boaPayload.datePeriod, boaPayload.periodOverrideMode);
        }
        emit('close:picker');
        return;
    }

    const payload = getPayload();
    emit('submit', payload, activeTab.value);
    emit('close:picker');
}

watch(() => props.modelValue?.aggregate, (newAggregate) => {
    if (newAggregate) {
        aggregate.value = { ...newAggregate };
    } else {
        aggregate.value = { ...defaultAggregateOptions };
    }
}, { deep: true });

watch(tabOptions, (newTabOptions) => {
    if (!newTabOptions.some(tab => tab.id === activeTab.value)) {
        setActiveTab((newTabOptions[0]?.id as AdvancedDatePickerTabsEnum | undefined) ?? AdvancedDatePickerTabsEnum.DATE);
    }
}, { immediate: true });

</script>

<style lang="scss" scoped>
.date-picker-wrapper {
    display: flex;
    flex-direction: column;
}

.date-selector {
    padding: var(--largeSpacing);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    width: var(--dateSelectorWidth);
    max-width: 100%;
    box-sizing: border-box;

    &.compact {
        width: min(42rem, var(--dateSelectorWidth));
    }

    &.boa-period {
        width: max-content;
    }

    @include responsive('sm') {
        width: 100%;
    }
}

.actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--defaultSpacing);
    padding: var(--defaultSpacing);
    width: 100%;
}

</style>
